<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="formState.edit ? updateButton : {}"
    :secondary-cta="{}"
    body-class="q-px-2x-md q-pt-xl"
    @submit.prevent="onSubmit"
  >
    <div class="col-12 q-mt-xl">
      <span class="text-md text-weight-medium text-neutral">
        Basic Information
      </span>
      <q-separator class="q-mb-sm" />
    </div>

    <div :class="formState.edit ? 'column reverse' : 'column'">
      <div class="row q-col-gutter-xl q-mb-xs">
        <div class="col-4">
          <PInput
            v-model.trim="formData.name"
            label="Contact Name"
            placeholder="Enter contact name"
            required
            class="text-capitalize"
            @update:model-value="
              (v) => {
                formData.name = capitalizePreservingSpecialChars(v);
              }
            "
          />
        </div>
        <div class="col-4">
          <PInput
            v-model.trim.number="formData.phone"
            class="prepend-bg"
            label="Contact No."
            placeholder="Enter 10 digit phone number"
            maxlength="10"
            :disable="formState.edit"
            :validations="{
              required: true,
              type: 'phoneNumber',
              length: 10,
            }"
            :rules="[validatePhone]"
            :loading="validatingPhone"
            debounce="300"
            hide-bottom-space
          >
            <template #prepend>
              <span class="text-sm">{{ isdCode }}</span>
            </template>

            <template #after>
              <div v-if="formState.edit" class="q-mt-xxs">
                <div v-if="formData.is_phone_verified">
                  <CheckIcon class="icon text-success-70" />
                  <PTooltip>Verified</PTooltip>
                </div>
                <div v-else-if="!!formData.phone">
                  <ExclamationIcon class="icon text-warning-60" />
                  <PTooltip>Unverified</PTooltip>
                </div>
              </div>
            </template>
          </PInput>
          <PBtn
            v-if="
              formState.edit && !formData.is_phone_verified && !!formData.phone
            "
            label="Verify"
            flat
            dense
            class="q-pa-none"
            @click="
              () => verifyVerificationCodeModal({ phone: formData.phone })
            "
          />
        </div>
        <div class="col-4">
          <PInput
            v-model.trim="formData.email"
            label="Contact Email"
            :disable="formData.is_email_verified"
            placeholder="Enter email"
            :validations="{
              type: 'email',
            }"
            :required="teamMembers"
            :rules="[validateEmail]"
            :loading="validatingEmail"
            debounce="300"
            @update:model-value="
              (v) => {
                formData.email = v.toLowerCase();
              }
            "
          >
            <template #after>
              <div v-if="formState.edit" class="q-mt-xxs">
                <div v-if="formData.is_email_verified">
                  <CheckIcon class="icon text-success-70" />
                  <PTooltip>Verified</PTooltip>
                </div>
                <div v-else-if="!!formData.email">
                  <ExclamationIcon class="icon text-warning-60" />
                  <PTooltip>Unverified</PTooltip>
                </div>
              </div>
            </template>
          </PInput>
        </div>
      </div>

      <div
        v-if="!teamMembers && ['buyer', 'seller'].includes(formData.role)"
        class="row q-col-gutter-lg q-mb-md"
      >
        <div v-if="formState.edit" class="col-6">
          <PInput
            v-model.trim="formData.shop_name"
            label="Registered Business Name"
            placeholder="Enter registered business name"
            required
            :disable="user?.businessDetails?.is_gst_registered ?? true"
            @update:model-value="
              (v) => {
                formData.shop_name = upperFirstFunc(v);
              }
            "
          />
        </div>
        <div :class="formState.edit ? 'col-3' : 'col-6'">
          <PSelectInput
            v-model="computedBusinessChannels"
            :options="unref(businessChannelsFilter.entities)"
            label="Business Channel"
            option-label="name"
            option-value="name"
            placeholder="Select business channel"
            required
            use-chips
            multiple
            @filter="businessChannelsFilter.onFilter"
            @filter-abort="businessChannelsFilter.onFilterAbort"
          />
        </div>
        <div :class="formState.edit ? 'col-3' : 'col-6'">
          <PSelectInput
            v-model.trim="formData.trade_name"
            :options="unref(tradeNameFilter.entities)"
            label="Trade Name"
            option-label="name"
            option-value="name"
            placeholder="E.g. Udaan"
            @filter="tradeNameFilter.onFilter"
            @filter-abort="tradeNameFilter.onFilterAbort"
          />
          <PBtn
            dense
            flat
            fluid
            link
            class="flex q-ml-auto"
            @click="addTradeName"
          >
            <AddIcon class="icon--sm q-mr-sm" />
            <span>Add new</span>
          </PBtn>
        </div>
      </div>
    </div>
    <div v-if="teamMembers" class="col-12 full-width">
      <PSelectInput
        v-model="formData.role"
        :options="unref(roleFilter.entities)"
        label="Select Role"
        placeholder="Select role"
        option-label="name"
        option-value="code"
        required
        :disable="formState.edit && !permissions?.role_management?.edit"
        @filter="roleFilter.onFilter"
        @filter-abort="roleFilter.onFilterAbort"
        @update:model-value="onUpdateRole"
      />
      <PBtn
        v-if="!formState.edit"
        dense
        flat
        fluid
        link
        class="text-decoration-none"
        :disable="!formData.role"
        @click="showPermissions"
      >
        <span>Preview Associated Permission</span>
      </PBtn>
    </div>
    <q-separator v-if="!teamMembers" class="q-mb-sm" />
    <div v-if="!teamMembers" class="row q-col-gutter-lg">
      <div class="col-12">
        <PField v-model="distributionType" label="Distribution Type" required>
          <template #control>
            <POptionGroup
              v-model="distributionType"
              color="primary-100"
              size="md"
              keep-color
              :options="distributionTypeOptions"
            />
          </template>
        </PField>
      </div>
    </div>
    <div v-if="distributionType === 'other' && !teamMembers" class="row">
      <div class="col-12">
        <PInput
          v-model.trim="formData.distribution_type"
          placeholder="Enter other distribution type"
          required
          @update:model-value="
            (v) => {
              formData.distribution_type = upperFirstFunc(v);
            }
          "
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import basicInfoFormModel from "src/modules/user/models/basicInfoForm";
import AddIcon from "assets/icons/add.svg";
import CheckIcon from "assets/icons/check-round-outline.svg";
import ExclamationIcon from "assets/icons/exclamation-round.svg";
import {
  capitalizePreservingSpecialChars,
  upperFirstFunc,
} from "assets/scripts/utilities/helpers";
import { useQuasar } from "quasar";
import { useFilter } from "src/composables/filter";
import { useTradeNameStore } from "src/stores/trade-name";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { validateUser } from "src/api/user";
import PBtn from "src/components/PBtn.vue";
import { unref } from "vue";
import { useContactVerification } from "../composables/contact-verification";

const $q = useQuasar();
const pForm = ref(null);

defineExpose({ pForm });

const { formState, user } = storeToRefs(useUserStore());
const { putUser, toggleUserForm, toggleTradeNameForm } = useUserStore();

const { verifyVerificationCodeModal } = useContactVerification({ user });
const { postTradeName } = useTradeNameStore();

const { roles } = storeToRefs(useRoleManagementStore());
const { getRolesForUser, getRoleById, toggleRoleForm } =
  useRoleManagementStore();
const { permissions } = storeToRefs(useAuthStore());

const onGoingOnboarding = inject("onGoingOnboarding");
const { hash } = useRouterUtilities();
const derivedRole = computed(() => {
  if (unref(hash) === "#team-members") return "";

  if (unref(hash) === "#customers") return "buyer";

  if (unref(hash) === "#vendors") return "seller";

  return "";
});

const computedEntity = computed(() => unref(user).basicInfo);

const { formData, isLoading, submitForm } = useForm({
  formModel: basicInfoFormModel(),
  entity: computedEntity,
  formRef: pForm,
  isForm: formState,
  put: putUser,
  toggleForm: toggleUserForm,
  editMode: unref(onGoingOnboarding),
});

const isAgentRole = computed(() => {
  return ["agent"].includes(unref(formData).role);
});

const distributionTypeOptions = ref([
  { label: "Wholesaler", value: "wholesaler" },
  { label: "Retailer", value: "retailer" },
  { label: "Distributor", value: "distributor" },
  { label: "Manufacturer", value: "manufacturer" },
  { label: "Other", value: "other" },
]);

const tradeNameFilter = useFilter({
  entity: "tradeName",
});

const addTradeName = () => {
  toggleTradeNameForm({
    open: true,
    level: 0,
    props: {
      toggle: { method: postTradeName },
    },
  });
};

const showPermissions = async () => {
  toggleRoleForm({
    open: true,
    edit: true,
    view: true,
    level: 1,
  });
};

let businessChannelsFilter = {};

watchEffect(() => {
  businessChannelsFilter = useFilter({
    entity: "businessVerticals",
    options: { params: { applies_to: unref(formData).role } },
  });
});

const roleFilter = useFilter({
  entities: computed(() => [
    ...unref(roles).map((role) => ({
      name: role.name,
      code: role.code,
      _id: role._id,
    })),
  ]),
  props: ["name"],
});

const findRoleId = (roleCode) => {
  const roleByCode = unref(roleFilter.entities).find(
    (role) => role.code === roleCode,
  );

  if (roleByCode) return roleByCode._id;
  else return "";
};

const computedBusinessChannels = computed({
  get: () => {
    const business_channels = Array.isArray(unref(formData).business_channel)
      ? unref(formData).business_channel
      : [unref(formData).business_channel];

    return business_channels;
  },
  set: (value) => {
    unref(formData).business_channel = value;
  },
});

const isdCode = ref("+91");

const onSubmit = async () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to edit these details ?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => await submitForm(unref(isAgentRole)),
    },
  });
};

const onUpdateRole = async (role) => {
  await getRoleById(findRoleId(role));

  unref(formData).is_internal_user = true;
};

const distributionType = computed({
  get() {
    if (
      ["wholesaler", "retailer", "manufacturer", "distributor"].includes(
        unref(formData).distribution_type,
      )
    ) {
      return unref(formData).distribution_type;
    } else {
      return "other";
    }
  },
  set(value) {
    if (value === "other") {
      unref(formData).distribution_type = "";
    } else {
      unref(formData).distribution_type = value;
    }
  },
});

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
  teamMembers: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const computedFormData = computed({
  get: function () {
    return props.modelValue;
  },
  set: function (updatedFormData) {
    emit("update:modelValue", updatedFormData);
  },
});

const validatingPhone = ref(false);
const validatingEmail = ref(false);
const isDuplicateValue = defineModel("isDuplicateValue", { type: Boolean });

const validatePhone = async (phone) => {
  try {
    const { organisation } = storeToRefs(useOrganisationStore());
    const org = unref(organisation);

    if (unref(user).basicInfo?.phone !== phone) {
      validatingPhone.value = true;

      await validateUser({
        phone,
        organisations: { internal_number: org?.internal_number, pan: org?.pan },
        role: derivedRole.value,
      });

      validatingPhone.value = false;
    }

    isDuplicateValue.value = false;

    return true;
  } catch (error) {
    validatingPhone.value = false;

    isDuplicateValue.value = true;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return error?.response?.data?.errors?.[0]?.msg;
    }

    return true;
  }
};

const validateEmail = async (email) => {
  try {
    if (unref(user).basicInfo?.email !== email) {
      validatingEmail.value = true;

      await validateUser({ email });

      validatingEmail.value = false;
    }

    isDuplicateValue.value = false;

    return true;
  } catch (error) {
    validatingEmail.value = false;

    isDuplicateValue.value = true;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return error?.response?.data?.errors?.[0]?.msg;
    }

    return true;
  }
};

const updateButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Update",
    type: "update",
  };
});

watch(
  formData,
  (data) => {
    computedFormData.value = data;
  },
  {
    immediate: true,
  },
);

watchEffect(() => {
  if (unref(formState).edit) {
    getRoleById(findRoleId(unref(formData).role));
  } else {
    formData.value.role = derivedRole.value;

    if (unref(derivedRole)) {
      getRoleById(findRoleId(unref(derivedRole)));
    }
  }
});

onBeforeMount(() => {
  getRolesForUser({
    params: {
      applicable_to: "internal",
    },
  });
});
</script>
